import { EDIT_TYPE_LABELS } from '../../../../static/constants.js';
import { PERSONNEL } from '../../../../static/keyWords.js';
import { messageManager } from '../../../System/messageManager/messageManager.js';

import {
  BUDGET_NAME_COLUMN,
  YEAR_COLUMN,
  ITEM_ID_COLUMN,
  ACCOUNT_ID_COLUMN,
  ACCOUNT_CODE_COLUMN,
  ACCOUNT_TYPE_COLUMN,
  DURATION_TYPE_COLUMN,
  ONGOING_COST_COLUMN,
  ONETIME_COST_COLUMN,
  NUMBER_OF_ITEMS_COLUMN,
  ACCOUNT_TYPES,
  DURATION_TYPES,
  DURATION_TYPE_LABELS,
  COST_NUMERIC_REGEX
} from '../budgetUploadHelpers.js';

export function generateStatusObj(errorMessage, normalizedValue) {
  const isError = Boolean(errorMessage);
  const status = {
    isValid: !isError,
    errorMessage: isError ? errorMessage : '',
    outcome: isError ? messageManager().BUDGET_UPLOAD.VALIDATION.FAIL_VALIDATION : messageManager().BUDGET_UPLOAD.VALIDATION.PASS_VALIDATION
  };

  if (normalizedValue !== undefined) {
    status.normalizedValue = normalizedValue;
  }

  return status;
}

export function validateEditColumn(cellData) {
  const value = cellData && cellData.length ? cellData.toLowerCase().trim() : '';
  if (
    value.toLowerCase() === EDIT_TYPE_LABELS.EDIT.toLowerCase() ||
    value.toLowerCase() === EDIT_TYPE_LABELS.DELETE.toLowerCase() ||
    value.toLowerCase() === EDIT_TYPE_LABELS.ADD.toLowerCase() ||
    value === EDIT_TYPE_LABELS.IGNORE
  ) {
    return generateStatusObj(false);
  }
  return generateStatusObj(
    messageManager().VALID_ACTION_COLUMN_ERROR([EDIT_TYPE_LABELS.ADD, EDIT_TYPE_LABELS.EDIT, EDIT_TYPE_LABELS.DELETE])
  );
}

export function validateBudgetNameColumn(cellData) {
  const value = cellData && cellData.length ? cellData.trim() : '';
  if (value.length) {
    return generateStatusObj(false);
  }
  return generateStatusObj(`${BUDGET_NAME_COLUMN} must be provided`);
}

export function validateYearColumn(cellData) {
  if (cellData === null || cellData === undefined) {
    return generateStatusObj(`${YEAR_COLUMN} must be provided`);
  }
  cellData = cellData.toString();
  const value = cellData && cellData.length ? cellData.trim() : '';
  if (!value.length) {
    return generateStatusObj(`${YEAR_COLUMN} must be provided`);
  }
  const year = Number(value);
  if (isNaN(year)) {
    return generateStatusObj(`${YEAR_COLUMN} must be a number value`);
  }
  if (year >= 1000 && year <= 9999) {
    return generateStatusObj(false);
  }
  return generateStatusObj('Please enter a year using YYYY format');
}

export function validateItemIdColumn(cellData, editType) {
  if (editType.toLowerCase() !== EDIT_TYPE_LABELS.EDIT.toLowerCase() && editType.toLowerCase() !== EDIT_TYPE_LABELS.DELETE.toLowerCase()) {
    return generateStatusObj(false);
  }
  if (cellData === null || cellData === undefined || cellData === '' || !cellData.toString().trim().length) {
    return generateStatusObj(`${ITEM_ID_COLUMN} must be provided`);
  }
  cellData = cellData.toString();
  const value = cellData && cellData.length ? cellData.trim() : '';
  const itemId = Number(value);
  if (isNaN(itemId)) {
    return generateStatusObj(`${ITEM_ID_COLUMN} must be a number value`);
  }
  if (itemId <= 0) {
    return generateStatusObj(`${ITEM_ID_COLUMN} must be a positive number`);
  }
  return generateStatusObj(false);
}

export function validateAccountIdColumn(cellData, editType) {
  if (editType.toLowerCase() !== EDIT_TYPE_LABELS.EDIT.toLowerCase()) {
    return generateStatusObj(false);
  }
  if (cellData === null || cellData === undefined || !cellData || !cellData.toString().trim().length) {
    return generateStatusObj(`${ACCOUNT_ID_COLUMN} must be provided`);
  }
  cellData = cellData.toString();
  const value = cellData && cellData.length ? cellData.trim() : '';
  const acctId = Number(value);
  if (isNaN(acctId)) {
    return generateStatusObj(`${ACCOUNT_ID_COLUMN} must be a number value`);
  }
  if (acctId <= 0) {
    return generateStatusObj(`${ACCOUNT_ID_COLUMN} must be a positive number`);
  }
  return generateStatusObj(false);
}

export function validateAccountCodeColumn(cellData, editType) {
  if (editType.toLowerCase() !== EDIT_TYPE_LABELS.EDIT.toLowerCase()) {
    return generateStatusObj(false);
  }
  cellData = cellData ? cellData.toString() : cellData;
  const value = cellData && cellData.length ? cellData.trim() : '';
  if (!value.length) {
    return generateStatusObj(`${ACCOUNT_CODE_COLUMN} must be provided`);
  }
  return generateStatusObj(false);
}

export function validateAccountTypeColumn(cellData, editType, currentStep) {
  if (editType.toLowerCase() !== EDIT_TYPE_LABELS.EDIT.toLowerCase() && editType.toLowerCase() !== EDIT_TYPE_LABELS.ADD.toLowerCase()) {
    return generateStatusObj(false);
  }
  cellData = cellData.toString();
  let value = cellData && cellData.length ? cellData.trim().toLowerCase() : '';
  if (!value.length) {
    return generateStatusObj(`${ACCOUNT_TYPE_COLUMN} must be provided`);
  }
  if (currentStep === PERSONNEL) {
    if (value === ACCOUNT_TYPES.EXPENSE) {
      return generateStatusObj(false);
    }
    return generateStatusObj(
      'Please specify a valid account type: Expense'
    );
  }
  if (value === ACCOUNT_TYPES.EXPENSE || value === ACCOUNT_TYPES.REVENUE) {
    return generateStatusObj(false);
  }
  return generateStatusObj(
    'Please specify a valid account type: Expense or Revenue'
  );
}

export function validateDurationTypeColumn(cellData, editType) {
  if (editType.toLowerCase() !== EDIT_TYPE_LABELS.EDIT.toLowerCase() && editType.toLowerCase() !== EDIT_TYPE_LABELS.ADD.toLowerCase()) {
    return generateStatusObj(false);
  }
  const value = normalizeDurationTypeValue(cellData);
  if (!value.length) {
    return generateStatusObj(`${DURATION_TYPE_COLUMN} must be provided`);
  }
  if (value === DURATION_TYPES.ONGOING) {
    return generateStatusObj(false, DURATION_TYPE_LABELS.ONGOING);
  }
  if (value === DURATION_TYPES.ONE_TIME) {
    return generateStatusObj(false, DURATION_TYPE_LABELS.ONE_TIME);
  }
  return generateStatusObj(
    `Please specify a valid ${DURATION_TYPE_COLUMN.toLocaleLowerCase()}: One time or Ongoing.`
  );
}

export function normalizeDurationTypeValue(cellData) {
  return (cellData || cellData === 0)
    ? cellData.toString().toLowerCase().replace(/\s+/g, '')
    : '';
}

export function validateOngoingCostColumn(cellData, editType) {
  if (editType.toLowerCase() !== EDIT_TYPE_LABELS.EDIT.toLowerCase() && editType.toLowerCase() !== EDIT_TYPE_LABELS.ADD.toLowerCase()) {
    return generateStatusObj(false);
  }
  cellData = cellData.toString();
  const value = cellData && cellData.length ? cellData.trim() : '';
  if (!value.length) {
    return generateStatusObj(
      `Please enter the line item's ${ONGOING_COST_COLUMN} formatted as follows: XXXXXXX.XX`
    );
  }
  const ongoingCost = Number(value);
  if (isNaN(ongoingCost)) {
    return generateStatusObj(`${ONGOING_COST_COLUMN} must be a number value`);
  }

  if (!COST_NUMERIC_REGEX.test(value)) {
    return generateStatusObj(
      `${ONGOING_COST_COLUMN} must be between -10 000 000 000 000.00 and 10 000 000 000 000.00`
    );
  }
  return generateStatusObj(false);
}

export function validateOneTimeCostColumn(cellData, editType) {
  if (editType.toLowerCase() !== EDIT_TYPE_LABELS.EDIT.toLowerCase() && editType.toLowerCase() !== EDIT_TYPE_LABELS.ADD.toLowerCase()) {
    return generateStatusObj(false);
  }
  cellData = cellData.toString();
  const value = cellData && cellData.length ? cellData.trim() : '';
  if (!value.length) {
    return generateStatusObj(
      `Please enter the line item's ${ONETIME_COST_COLUMN} formatted as follows: XXXXXXX.XX`
    );
  }
  const oneTimeCost = Number(value);
  if (isNaN(oneTimeCost)) {
    return generateStatusObj(`${ONETIME_COST_COLUMN} must be a number value`);
  }

  if (!COST_NUMERIC_REGEX.test(value)) {
    return generateStatusObj(
      `${ONETIME_COST_COLUMN} must be between -10 000 000 000 000.00 and 10 000 000 000 000.00`
    );
  }
  return generateStatusObj(false);
}

export function validateNumberOfItemsColumn(cellData, editType) {
  if (editType.toLowerCase() !== EDIT_TYPE_LABELS.EDIT.toLowerCase() && editType.toLowerCase() !== EDIT_TYPE_LABELS.ADD.toLowerCase()) {
    return generateStatusObj(false);
  }
  if (cellData === null || cellData === undefined) {
    return generateStatusObj(`${NUMBER_OF_ITEMS_COLUMN} must be provided`);
  }
  cellData = cellData.toString();
  const value = cellData && cellData.length ? cellData.trim() : '';
  if (!value.length) {
    return generateStatusObj(`${NUMBER_OF_ITEMS_COLUMN} must be provided`);
  }
  const numberOfItems = Number(value);
  if (isNaN(numberOfItems)) {
    return generateStatusObj(`${NUMBER_OF_ITEMS_COLUMN} must be a number value`);
  }
  if (numberOfItems < 0) {
    return generateStatusObj(`${NUMBER_OF_ITEMS_COLUMN} must be a non-negative number`);
  }
  return generateStatusObj(false);
}

export function validateCostCenterColumn(cellData, editType, columnName) {

  if (editType.toLowerCase() !== EDIT_TYPE_LABELS.EDIT.toLowerCase() && editType.toLowerCase() !== EDIT_TYPE_LABELS.ADD.toLowerCase()) {
    return generateStatusObj(false);
  }
  if (cellData === null || cellData === undefined) {
    return generateStatusObj(`${columnName} must be provided`);
  }
  cellData = cellData.toString();
  const value = cellData && cellData.length ? cellData.trim() : '';
  if (!value.length) {
    return generateStatusObj(`${columnName} must be provided`);
  }
  return generateStatusObj(false);
}

export function validateFundTypeColumn(cellData, editType) {
  if (editType.toLowerCase() !== EDIT_TYPE_LABELS.EDIT.toLowerCase() && editType.toLowerCase() !== EDIT_TYPE_LABELS.ADD.toLowerCase()) {
    return generateStatusObj(false);
  }
  if (cellData === null || cellData === undefined) {
    return generateStatusObj('Fund must be provided');
  }
  cellData = cellData.toString();
  const value = cellData && cellData.length ? cellData.trim() : '';
  if (!value.length) {
    return generateStatusObj('Fund must be provided');
  }
  return generateStatusObj(false);
}
